import React, { useEffect, useMemo, useState } from "react";
import { Breadcrumb } from "../../components/ui";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { Button } from "primereact/button";
import { Chart } from "primereact/chart";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import toast from "react-hot-toast";
import { DateRangeFilter } from "../../components/Filters/DateRangeFilter";
import {
  BenefitsMetricsKpiCards,
  BenefitsMetricsLoadingOverlay,
  BenefitsMetricsRefreshingIndicator,
  BenefitsMetricsTenantScopeHeader,
} from "./components/BenefitsMetricsWidgets";
import { BenefitMetricsTopPartner } from "../../models/benefitsMetrics";
import { TENANT_CONTEXT_ALL } from "../../store/services/api";
import { useGetBenefitMetricsQuery } from "../../store/services/benefits";
import { useBenefitsMetricsExport } from "../../hooks/useBenefitsMetricsExport";
import { useTenantBrandColors } from "../../hooks/useTenantBrandColors";
import { useTenantScope } from "../../hooks/useTenantScope";
import { getApiErrorMessage } from "../../utils/functions/api-errors";
import {
  defaultBenefitsMetricsDateFrom,
  defaultBenefitsMetricsDateTo,
  endOfDayIso,
  startOfDayIso,
} from "../../utils/functions/benefitsMetricsDateRange";
import {
  benefitsMetricsChartOptions,
  benefitsMetricsMethodChartOptions,
  benefitsMetricsTenantMethodChartOptions,
  benefitsMetricsTenantDayChartOptions,
  buildBenefitsMetricsDetailPath,
  buildBenefitsMetricsTenantSeries,
  buildRedemptionsByDayChartData,
  buildRedemptionsByMethodChartData,
  buildTenantRedemptionsByDayChartData,
  buildTenantRedemptionsByMethodChartData,
  formatBenefitsMetricsShare,
  hasBenefitsMetricsTenantBreakdown,
  hasRedemptionsByMethod,
  tenantMethodChartHeight,
} from "../../utils/functions/benefitsMetricsPresentation";
import { useBenefitsMetricsQueryDisplay } from "../../hooks/useBenefitsMetricsQueryDisplay";
import { getTenantBrandColor } from "../../utils/functions/tenantThemeColor";

const parseIsoDateParam = (value: string | null): Date | null => {
  if (!value) {
    return null;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed;
};

interface BenefitsMetricsDetailLocationState {
  partnerName?: string | null;
}

export const BenefitsMetricsDetail: React.FC = () => {
  const scope = useTenantScope();
  const selectedTenantId = scope.tenantId;
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const locationState = location.state as
    | BenefitsMetricsDetailLocationState
    | null;
  const partnerId = searchParams.get("partnerId") ?? undefined;
  const dateFromParam = searchParams.get("dateFrom");
  const dateToParam = searchParams.get("dateTo");

  const dateFrom =
    parseIsoDateParam(dateFromParam) ?? defaultBenefitsMetricsDateFrom();
  const dateTo =
    parseIsoDateParam(dateToParam) ?? defaultBenefitsMetricsDateTo();
  const [todayDate] = useState(defaultBenefitsMetricsDateTo);

  useEffect(() => {
    const hasTenantInUrl = searchParams.has("tenantId");
    if (dateFromParam && dateToParam && !hasTenantInUrl) {
      return;
    }

    const next = new URLSearchParams(searchParams);
    if (!dateFromParam) {
      next.set("dateFrom", startOfDayIso(defaultBenefitsMetricsDateFrom()));
    }
    if (!dateToParam) {
      next.set("dateTo", endOfDayIso(defaultBenefitsMetricsDateTo()));
    }
    next.delete("tenantId");
    setSearchParams(next, { replace: true });
  }, [dateFromParam, dateToParam, searchParams, setSearchParams]);

  const setDateFrom = (value: Date | null) => {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set("dateFrom", startOfDayIso(value));
    } else {
      next.delete("dateFrom");
    }
    setSearchParams(next, { replace: true });
  };

  const setDateTo = (value: Date | null) => {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set("dateTo", endOfDayIso(value));
    } else {
      next.delete("dateTo");
    }
    setSearchParams(next, { replace: true });
  };

  const queryParams = {
    dateFrom: startOfDayIso(dateFrom),
    dateTo: endOfDayIso(dateTo),
    ...(partnerId ? { partnerId } : {}),
    ...(selectedTenantId ? { tenantId: selectedTenantId } : {}),
  };

  const { data, currentData, isLoading, isFetching, isError, error } =
    useGetBenefitMetricsQuery(queryParams, { skip: scope.loading });
  const { metrics, isBlockingLoading, isRefreshing } = useBenefitsMetricsQueryDisplay({
    data,
    currentData,
    isFetching,
    scopeKey: selectedTenantId ?? TENANT_CONTEXT_ALL,
  });
  const emptyStateMessage = isError
    ? "No pudimos cargar las métricas"
    : "Sin canjes en el rango seleccionado";
  const { exportMetrics, isExporting } = useBenefitsMetricsExport();

  useEffect(() => {
    if (isError) {
      toast.error(getApiErrorMessage(error) ?? "Error al cargar métricas");
    }
  }, [isError, error]);

  const isPartnerView = Boolean(partnerId);

  const tenantBrandColors = useTenantBrandColors();
  const tenantSeries = useMemo(
    () => buildBenefitsMetricsTenantSeries(metrics?.by_tenant),
    [metrics?.by_tenant]
  );
  const showTenantBreakdown =
    !selectedTenantId && hasBenefitsMetricsTenantBreakdown(tenantSeries);
  const selectedTenantColor = selectedTenantId
    ? tenantBrandColors[selectedTenantId] ?? getTenantBrandColor(null)
    : null;
  const methodChartData = useMemo(
    () =>
      showTenantBreakdown
        ? buildTenantRedemptionsByMethodChartData(tenantSeries)
        : buildRedemptionsByMethodChartData(
            metrics?.redemptions_by_method,
            selectedTenantColor
          ),
    [showTenantBreakdown, tenantSeries, metrics?.redemptions_by_method, selectedTenantColor]
  );
  const redemptionsByDay = useMemo(
    () => metrics?.redemptions_by_day ?? [],
    [metrics?.redemptions_by_day]
  );
  const dailyChartData = useMemo(
    () =>
      showTenantBreakdown
        ? buildTenantRedemptionsByDayChartData(tenantSeries)
        : buildRedemptionsByDayChartData(redemptionsByDay, selectedTenantColor, {
            dateFrom,
            dateTo,
          }),
    [showTenantBreakdown, tenantSeries, redemptionsByDay, selectedTenantColor, dateFrom, dateTo]
  );
  const chartHeight = showTenantBreakdown ? "320px" : "280px";
  // El de metodos crece con el metodo que mas tenants tenga: las barras no se achican
  // cuando canjean varios, se agranda la caja.
  const methodChartHeight = showTenantBreakdown
    ? `${tenantMethodChartHeight(tenantSeries)}px`
    : chartHeight;

  const selectedPartnerName = partnerId
    ? locationState?.partnerName ??
      metrics?.top_partners?.find((partner) => partner.id === partnerId)?.name ??
      partnerId
    : null;

  const partnerNameBody = (row: BenefitMetricsTopPartner) => (
    <Link
      to={buildBenefitsMetricsDetailPath({
        partnerId: row.id,
        dateFrom: startOfDayIso(dateFrom),
        dateTo: endOfDayIso(dateTo),
      })}
      state={{ partnerName: row.name }}
      className="text-primary no-underline hover:underline"
    >
      {row.name ?? row.id}
    </Link>
  );

  const tenantNameBody = (row: BenefitMetricsTopPartner) =>
    row.tenant_name ?? row.tenant_id;

  const shareBody = (row: BenefitMetricsTopPartner) =>
    formatBenefitsMetricsShare(row.redemptions, metrics?.total_redemptions);

  const methodChart = hasRedemptionsByMethod(metrics?.redemptions_by_method) ? (
    <div style={{ height: methodChartHeight }}>
      <Chart
        key={`benefits-metrics-method-${selectedTenantId ?? "all"}-${selectedTenantColor ?? "default"}`}
        type="bar"
        data={methodChartData}
        options={
          showTenantBreakdown
            ? benefitsMetricsTenantMethodChartOptions
            : benefitsMetricsMethodChartOptions
        }
        className="h-full w-full"
      />
    </div>
  ) : (
    <p className="text-color-secondary m-0">
      {isBlockingLoading ? "" : emptyStateMessage}
    </p>
  );

  return (
    <div className="p-4">
      <Breadcrumb items={[{ label: "Métricas de beneficios", to: "/benefits/metrics" }, { label: "Detalle" }]} />
      <div className="p-card p-4 relative overflow-hidden">
        <BenefitsMetricsLoadingOverlay visible={scope.loading || isBlockingLoading} />

        <BenefitsMetricsTenantScopeHeader tenantId={selectedTenantId} />

        <div className="mb-4 flex flex-column md:flex-row md:align-items-center md:justify-content-between gap-2">
          <div>
            <div className="flex align-items-center gap-2">
              <h1 className="text-2xl font-semibold text-900 m-0">
                {isPartnerView ? "Detalle del partner" : "Detalle de métricas"}
              </h1>
              <BenefitsMetricsRefreshingIndicator visible={isRefreshing} />
            </div>
            {selectedPartnerName && (
              <p className="text-color-secondary m-0 mt-2">
                {selectedPartnerName}
              </p>
            )}
          </div>
          <div className="flex align-items-center gap-3">
            <Button
              type="button"
              label="Exportar CSV"
              icon="pi pi-download"
              onClick={() => exportMetrics(queryParams)}
              disabled={scope.loading || isBlockingLoading || isExporting}
              loading={isExporting}
              data-testid="export-benefits-metrics-detail-button"
            />
            <Link
              to="/benefits/metrics"
              className="text-primary no-underline hover:underline"
            >
              Volver al dashboard
            </Link>
          </div>
        </div>

        {/* Por encima del overlay: en una carga fria el dato queda tapado, pero el
            usuario tiene que poder seguir moviendo los filtros. */}
        <div className="grid mb-4 relative" style={{ maxWidth: "480px", zIndex: 11 }}>
          <DateRangeFilter
            variant="stacked"
            columnClassName="col-12 sm:col-6"
            dateFrom={dateFrom}
            dateTo={dateTo}
            onDateFromChange={setDateFrom}
            onDateToChange={setDateTo}
            disabled={isLoading}
            startDateId="benefits-metrics-detail-date-from"
            endDateId="benefits-metrics-detail-date-to"
            maxDateFrom={todayDate}
            maxDateTo={todayDate}
            minDateTo={dateFrom}
            showClearButtons
          />
        </div>

        <BenefitsMetricsKpiCards
          totalActiveBenefits={metrics?.total_active_benefits}
          totalRedemptions={metrics?.total_redemptions}
        />

        {isPartnerView ? (
          <div className="grid">
            <div className="col-12 lg:col-6 mb-4 lg:mb-0">
              <h2 className="text-xl font-semibold text-900 mt-0 mb-3">
                Canjes por método
              </h2>
              {methodChart}
            </div>
            <div className="col-12 lg:col-6">
              <h2 className="text-xl font-semibold text-900 mt-0 mb-3">
                Canjes por día
              </h2>
              {redemptionsByDay.length ? (
                <div style={{ height: chartHeight }}>
                  <Chart
                    key={`benefits-metrics-detail-day-${selectedTenantId ?? "all"}-${selectedTenantColor ?? "default"}`}
                    type="line"
                    data={dailyChartData}
                    options={
                      showTenantBreakdown
                        ? benefitsMetricsTenantDayChartOptions
                        : benefitsMetricsChartOptions
                    }
                    className="h-full w-full"
                  />
                </div>
              ) : (
                <p className="text-color-secondary m-0">
                  {isBlockingLoading ? "" : emptyStateMessage}
                </p>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className="mb-4">
              <h2 className="text-xl font-semibold text-900 mt-0 mb-3">
                Métricas por partner
              </h2>
              <DataTable
                value={metrics?.top_partners ?? []}
                dataKey="id"
                emptyMessage="Sin datos"
                size="small"
              >
                <Column header="Nombre" body={partnerNameBody} />
                {showTenantBreakdown ? (
                  <Column header="Tenant" body={tenantNameBody} />
                ) : null}
                <Column field="redemptions" header="Canjes" />
                <Column header="% del total" body={shareBody} />
              </DataTable>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-900 mt-0 mb-3">
                Canjes por método
              </h2>
              {methodChart}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
